import React from 'react';
import {
  Box,
  Typography,
  Card,
  Divider,
  CircularProgress,
  styled
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import { useTranslation } from 'react-i18next';

const CHART_COLORS = ['#4CAF50', '#1976D2', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#F44336', '#795548'];

const ChartContainer = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  '& .MuiTypography-h6': {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
}));

interface BranchSalesSummary {
  branchId: string | null;
  totalBalance: number;
  transactionCount: number;
}

interface Branch {
  id?: string;
  branchId?: string;
  name: string;
}

interface SalesDistributionChartProps {
  data: BranchSalesSummary[];
  loading: boolean;
  branches: Branch[];
}

const SalesDistributionChart: React.FC<SalesDistributionChartProps> = ({ data, loading, branches }) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'TRY' }).format(value);
  };

  const chartData = data
    .filter(s => s.totalBalance > 0)
    .map((summary) => {
      const branchName = summary.branchId
        ? (branches.find((b) => (b.id || b.branchId) === summary.branchId)?.name || summary.branchId)
        : t('dashboard.generalLabel');
      return {
        name: branchName.length > 18 ? `${branchName.substring(0, 18)}...` : branchName,
        fullName: branchName,
        value: summary.totalBalance,
      };
    });

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">{item.fullName}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: payload[0].color, borderRadius: '50%' }} />
            <Typography variant="body2">{formatCurrency(item.value)} (%{percent})</Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <ChartContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          {t('dashboard.salesDistributionChartTitle')}
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 160 }}>
          <CircularProgress />
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 160 }}>
          <Typography variant="body2" color="textSecondary">{t('dashboard.chartNoData')}</Typography>
        </Box>
      ) : (
        // Esnek yükseklik: kart büyütülünce grafik de büyür
        <Box sx={{ flexGrow: 1, minHeight: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius="70%"
              fill="#8884d8"
              dataKey="value"
              labelLine={false}
              label={false}
            >
              {chartData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, _entry, index) => {
                const percent = total > 0 ? ((chartData[index].value / total) * 100).toFixed(0) : 0;
                return `${value} (%${percent})`;
              }}
              wrapperStyle={{ fontSize: '12px' }}
            />
          </PieChart>
        </ResponsiveContainer>
        </Box>
      )}
    </ChartContainer>
  );
};

export default SalesDistributionChart;
